import Router from 'next/router';
import React, { lazy } from 'react';

import { SUCCESS_SEVERITY } from '@glass/common/modules/constants/severities';
import { SAVE_PROGRESS_EMAIL } from '@glass/common/modules/email/emailIds';
import { MOST_RECENT_RESUME_ID } from '@glass/common/modules/tracking/constants/propertyKeys';
import pathToURL from '@glass/common/modules/url/pathToURL';
import { selectUser } from '@glass/web/modules/auth';
import resetGlobalDialogAction from '@glass/web/modules/globalDialog/resetGlobalDialogAction';
import queueGlobalSnackbarAction from '@glass/web/modules/globalSnackbar/queueGlobalSnackbarAction';

import { CREATE_ACCOUNT_ACTION } from '@glass/shared/modules/auth/authActionTypes';
import sendCommunicationAction from '@glass/shared/modules/auth/sendCommunicationAction';
import promptAuthAction from '@glass/shared/modules/authForm/actions/promptAuthAction';
import setAuthPropsAction from '@glass/shared/modules/authForm/actions/setAuthPropsAction';
import { CHILDREN_AUTH_VIEW } from '@glass/shared/modules/authForm/authViews';
import { PROMPT_SAVE_PROGRESS } from '@glass/shared/modules/resumes/resumesReducer';
import Tracking from '@glass/shared/modules/tracking/Tracking';

export const loadSaveProgressBaseDialog = () =>
  import('@glass/shared/components/ResumeBuilder/SaveProgressBaseDialog');

const LazySaveProgressBaseDialog = lazy(loadSaveProgressBaseDialog);

const promptSaveProgressAction =
  ({ requirePassword = false, next = false, resumeId, title, reason, onExit, onAuth } = {}) =>
  (dispatch, getState, { tracking }) => {
    const user = selectUser(getState());
    tracking.track(Tracking.EVENTS.SAVE_PROGRESS, {
      action: Tracking.ACTIONS.START,
      label: reason,
      requirePasswordProp: requirePassword,
    });

    const handleClose = () => {
      dispatch(resetGlobalDialogAction());
      dispatch(setAuthPropsAction());
      tracking.track(Tracking.EVENTS.SAVE_PROGRESS, {
        action: Tracking.ACTIONS.END,
        label: reason,
      });
    };

    const handleExit = async (result) => {
      handleClose();
      if (typeof onExit === 'function') {
        await Promise.resolve(onExit(result));
      }
    };

    const handleAuth = async (result) => {
      const { query: ctaQuery } = pathToURL(Router.asPath, true);
      const emailResumeId = resumeId || ctaQuery.resumeId;
      tracking.track(Tracking.EVENTS.SAVE_PROGRESS, {
        action: Tracking.ACTIONS.AUTH,
        label: reason,
        [MOST_RECENT_RESUME_ID]: emailResumeId,
      });
      dispatch(
        sendCommunicationAction({
          email: result.email || user?.email,
          emailId: SAVE_PROGRESS_EMAIL,
        }),
      );

      dispatch(
        queueGlobalSnackbarAction({
          severity: SUCCESS_SEVERITY,
          title: 'A link to your $(resumeWords.singular) has been sent to your inbox',
        }),
      );
      handleClose();
      if (typeof onAuth === 'function') {
        await Promise.resolve(onAuth(result)).catch(tracking.exception);
      }
    };

    const authProps = {
      requirePassword,
      next,
      inDialog: true,
      view: CHILDREN_AUTH_VIEW,
      actionType: CREATE_ACCOUNT_ACTION,
      onAuth: handleAuth,
      onExit: handleExit,
      children: (
        <LazySaveProgressBaseDialog
          requirePassword={requirePassword}
          resumeId={resumeId}
          title={title}
          onAuth={handleAuth}
          onExit={handleExit}
        />
      ),
    };
    dispatch(promptAuthAction(authProps));
    dispatch({ type: PROMPT_SAVE_PROGRESS, payload: { reason } });
  };

export default promptSaveProgressAction;
